import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { dashboardV2QueryKeys } from '../api/queryKeys';
import { MissingTimesheetShift, MissingTimesheetWorker } from '../api/types';
import EnterHoursDialog from './EnterHoursDialog';

import { axiosRubyClient } from '@/util/axios/axiosClient';

// The Resolve Timesheet workflow, opened by the Missing Timesheet pencil
// (GRAVY-10330).
//
// ## Why this exists rather than importing the Job page's modal
//
// The QA retest was right: the pencil opened Enter Hours directly and so
// re-created ONE branch of a three-branch workflow. Dismiss Worker and Mark as
// No-Show were unreachable from the queue.
//
// The obvious fix — render `Job/WorkerTable/WorkerMobileView/ResolveTimesheetModal`
// here — breaks two of this ticket's OWN acceptance criteria:
//
//   * that modal's Enter Hours posts no `shiftId`, so the server falls back to
//     the job's FIRST shift. On a multi-shift job that resolves the wrong
//     occurrence, and "the exact selected shift_id" is an explicit criterion
//     (GRAVY-10307 added the occurrence for exactly this reason);
//   * its no-show branch ends in `window.location.reload()`, which would throw
//     away the queue's filters, page and tab — "filters, pagination, scroll
//     position, and Dashboard tab context are preserved" is also a criterion.
//
// So this reuses the WORKFLOW rather than the component: the same first
// question, the same three options in the same order, the same endpoints, the
// same payloads and the same dismissal reasons. What differs is only what had
// to: the occurrence is carried through, and completion invalidates the
// dashboard queries instead of reloading the browser.
//
// If the Job page's modal is ever refactored to take a shift and to report
// completion through a callback, this should collapse into it.

type Props = {
  shift: MissingTimesheetShift;
  worker: MissingTimesheetWorker;
  onClose: () => void;
};

type Action = '' | 'Enter Hours' | 'Dismiss Worker' | 'Mark as No-Show';

/** The Job page's own list, in its order (`DismissWorkerModal`). */
const DISMISSAL_REASONS = [
  'No longer needed',
  'Do not meet requirements',
  'Performance issue',
  'Late',
];

const ResolveTimesheetDialog = ({ shift, worker, onClose }: Props) => {
  const [action, setAction] = useState<Action>('');
  const [step, setStep] = useState<'choose' | Action>('choose');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Dismiss Worker fields, mirroring the Job page's modal.
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [blockWorker, setBlockWorker] = useState(false);

  const queryClient = useQueryClient();
  const openerRef = useRef<Element | null>(
    typeof document === 'undefined' ? null : document.activeElement,
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      (openerRef.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);

  const name = worker.name || 'this worker';

  /** Both write paths end the same way: refresh, do not reload. */
  const finish = async () => {
    await queryClient.invalidateQueries({
      queryKey: dashboardV2QueryKeys.root,
    });
    onClose();
  };

  const post = async (url: string, body: Record<string, unknown>) => {
    setSubmitting(true);
    setError(null);
    try {
      await axiosRubyClient.post(url, body);
      await finish();
    } catch (e) {
      setError(
        (e as { response?: { data?: { errors?: string[] } } })?.response?.data
          ?.errors?.[0] ?? 'Could not complete this action. Please try again.',
      );
      setSubmitting(false);
    }
  };

  // Enter Hours is its own dialog — the occurrence-exact one that already
  // carries the role-specific Save Changes / Approve / Resolve outcomes.
  if (step === 'Enter Hours') {
    return <EnterHoursDialog shift={shift} worker={worker} onClose={onClose} />;
  }

  return (
    <div className="dashv2-ehoverlay" role="presentation">
      <div
        className="dashv2-ehdialog"
        role="dialog"
        aria-modal="true"
        aria-label={`Resolve ${name}'s timesheet`}
        data-testid="resolveTimesheetDialog"
      >
        {step === 'choose' && (
          <>
            <h2 className="dashv2-ehtitle">
              How do you want to resolve {name}&apos;s timesheet?
            </h2>

            <label className="dashv2-ehfield">
              <span>Action</span>
              <select
                className="dashv2-ehinput"
                data-testid="resolveAction"
                value={action}
                onChange={(e) => {
                  setAction(e.target.value as Action);
                  setError(null);
                }}
              >
                <option value="" disabled>
                  Select an action
                </option>
                {/* Same three, in the Job page's order. */}
                <option value="Enter Hours">Enter Hours</option>
                <option value="Dismiss Worker">Dismiss Worker</option>
                <option value="Mark as No-Show">Mark as No-Show</option>
              </select>
            </label>

            {error && (
              <p className="dashv2-eherror" role="alert">
                {error}
              </p>
            )}

            <div className="dashv2-ehfoot">
              <button
                type="button"
                className="dashv2-ehcancel"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dashv2-ehresolve"
                onClick={() => {
                  if (!action) {
                    setError('Select an action to continue.');
                    return;
                  }
                  setStep(action);
                }}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {step === 'Mark as No-Show' && (
          <>
            <h2 className="dashv2-ehtitle">Mark {name} as a no-show?</h2>
            <p className="dashv2-ehnote">
              This records that {name} did not work the shift. They will move to
              the No Show queue and no timesheet will be owed.
            </p>
            {error && (
              <p className="dashv2-eherror" role="alert">
                {error}
              </p>
            )}
            <div className="dashv2-ehfoot">
              <button
                type="button"
                className="dashv2-ehcancel"
                onClick={() => setStep('choose')}
                disabled={submitting}
              >
                Back
              </button>
              <button
                type="button"
                className="dashv2-ehresolve"
                disabled={submitting}
                onClick={() =>
                  post('/api/admin/job_workers/mark_as_no_show', {
                    jobId: shift.job_id,
                    workerId: worker.worker_id,
                  })
                }
              >
                {submitting ? 'Saving…' : 'Mark as No-Show'}
              </button>
            </div>
          </>
        )}

        {step === 'Dismiss Worker' && (
          <>
            <h2 className="dashv2-ehtitle">Dismiss {name}</h2>

            <label className="dashv2-ehfield">
              <span>Reason</span>
              <select
                className="dashv2-ehinput"
                data-testid="dismissReason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                <option value="" disabled>
                  Select a reason
                </option>
                {DISMISSAL_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>

            <label className="dashv2-ehfield">
              <span>Details</span>
              <textarea
                className="dashv2-ehinput"
                data-testid="dismissDetails"
                rows={3}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
              />
            </label>

            <label className="dashv2-ehcheck">
              <input
                type="checkbox"
                checked={blockWorker}
                data-testid="dismissBlock"
                onChange={(e) => setBlockWorker(e.target.checked)}
              />
              <span>Block this worker from future shifts with this client</span>
            </label>

            {error && (
              <p className="dashv2-eherror" role="alert">
                {error}
              </p>
            )}

            <div className="dashv2-ehfoot">
              <button
                type="button"
                className="dashv2-ehcancel"
                onClick={() => setStep('choose')}
                disabled={submitting}
              >
                Back
              </button>
              <button
                type="button"
                className="dashv2-ehresolve"
                disabled={submitting}
                onClick={() => {
                  // The Job page requires a reason before it will submit.
                  if (!reason) {
                    setError('Select a reason for the dismissal.');
                    return;
                  }
                  post('/api/admin/job_workers/dismiss', {
                    workerId: worker.worker_id,
                    jobId: shift.job_id,
                    dismissType: reason,
                    reasonForDismissal: details,
                    shouldBlockWorker: blockWorker,
                  });
                }}
              >
                {submitting ? 'Saving…' : 'Dismiss Worker'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ResolveTimesheetDialog;
